import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { CollectionTradeStats, CollectionFloorPrice } from '../lib/api';

function formatTon(nanotons: string | null): string {
  if (!nanotons) return '—';
  return (Number(nanotons) / 1e9).toFixed(2);
}

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'stable' }) {
  if (trend === 'up') return <span className="text-success font-bold" title="Trending up">↑</span>;
  if (trend === 'down') return <span className="text-error font-bold" title="Trending down">↓</span>;
  return <span className="text-text-muted" title="Stable">→</span>;
}

function TradeStatsRow({ stat }: { stat: CollectionTradeStats }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-b-0">
      <div className="min-w-0">
        <span className="text-sm font-medium text-text-primary truncate block">{stat.collectionName}</span>
        <span className="text-xs text-text-muted">{stat.tradeCount} trade{stat.tradeCount !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right">
          <span className="text-sm font-semibold text-text-primary">{formatTon(stat.lastTradeTonNano)} TON</span>
          {stat.avgTradeTonNano && (
            <span className="text-xs text-text-muted block">avg {formatTon(stat.avgTradeTonNano)}</span>
          )}
        </div>
        <TrendIcon trend={stat.trend} />
      </div>
    </div>
  );
}

function FloorPriceRow({ fp }: { fp: CollectionFloorPrice }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-b-0">
      <div className="min-w-0">
        <span className="text-sm font-medium text-text-primary truncate block">{fp.collectionName}</span>
        <span className="text-xs text-text-muted">{fp.activeLotCount} active deal{fp.activeLotCount !== 1 ? 's' : ''}</span>
      </div>
      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 flex-shrink-0">
        Floor: {formatTon(fp.floorTonNano)} TON
      </span>
    </div>
  );
}

export function MarketDataPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['market-stats'],
    queryFn: () => api.getMarketStats(),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="border border-border rounded-xl p-4 mb-6 animate-pulse">
        <div className="h-4 bg-surface-alt rounded w-1/3 mb-3" />
        <div className="space-y-3">
          <div className="h-10 bg-surface-alt rounded" />
          <div className="h-10 bg-surface-alt rounded" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const hasTradeStats = data.tradeStats.length > 0;
  const hasFloorPrices = data.floorPrices.length > 0;

  if (!hasTradeStats && !hasFloorPrices) return null;

  const volumeTon = (Number(data.summary.totalVolumeTonNano) / 1e9).toFixed(1);

  return (
    <div className="border border-border rounded-xl p-4 mb-6 bg-surface-raised">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">Market Intelligence</h3>
        <div className="flex gap-3 text-xs text-text-muted">
          <span>{data.summary.totalTrades} trades</span>
          <span>{volumeTon} TON vol.</span>
          {data.summary.last24hTrades > 0 && (
            <span className="text-success-fg">{data.summary.last24hTrades} today</span>
          )}
        </div>
      </div>

      {hasTradeStats && (
        <div className="mb-4">
          <h4 className="text-xs text-text-muted mb-1">Recent prices</h4>
          <div className="divide-y divide-border/50">
            {data.tradeStats.slice(0, 5).map((stat) => (
              <TradeStatsRow key={stat.collectionName} stat={stat} />
            ))}
          </div>
        </div>
      )}

      {hasFloorPrices && (
        <div>
          <h4 className="text-xs text-text-muted mb-1">Floor prices</h4>
          <div className="divide-y divide-border/50">
            {data.floorPrices.slice(0, 5).map((fp) => (
              <FloorPriceRow key={fp.collectionName} fp={fp} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
